import { useEffect, useRef } from 'react';
import { keepPreviousData, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import aiReportsService, { AIReport, GenerateReportRequest } from '../services/ai-reports.service';

const STALE_MS = 30_000;

// aiReportsService.getReports() only accepts organization filters and resolves
// APIResponse<AIReport[]> — a flat array. The DRF pagination envelope
// (count/next/previous) the backend actually returns is discarded inside the
// service, and there is no status query param anywhere in the stack. There is
// nothing for the server to filter or page on from here, so both are applied
// client-side against the single fetch below.
export const REPORTS_PAGE_SIZE = 10;

/**
 * AIReport carries no `status` field — only `is_complete: boolean` and
 * `error_message?: string` (report generation is synchronous, so a row is
 * persisted already complete or already failed; GENERATING only shows up for
 * a row that got interrupted mid-generation). This derives the status value
 * the ledger surfaces from those two fields.
 */
export type ReportStatusValue = 'GENERATING' | 'FAILED' | 'COMPLETED';
export type ReportStatusFilter = 'ALL' | ReportStatusValue;

export const REPORT_STATUS_VALUES: ReportStatusValue[] = ['GENERATING', 'FAILED', 'COMPLETED'];

export function reportStatus(report: Pick<AIReport, 'is_complete' | 'error_message'>): ReportStatusValue {
  if (report.error_message) return 'FAILED';
  if (!report.is_complete) return 'GENERATING';
  return 'COMPLETED';
}

/**
 * Mirrors the apiParamsFor(...) shape used by the register/task hooks: which
 * filter keys would be sent, omitting status for 'ALL'. Kept pure and exported
 * so the omission rule is independently testable even though — per the module
 * comment above — the current service has nowhere to forward it server-side.
 */
export function apiParamsFor(status: ReportStatusFilter): { status?: ReportStatusValue } {
  return status === 'ALL' ? {} : { status };
}

export interface ReportsPageParams {
  status: ReportStatusFilter;
  page: number;
}

export interface PaginatedReports {
  count: number;
  next: string | null;
  previous: string | null;
  results: AIReport[];
}

export function useReportsPage(params: ReportsPageParams) {
  return useQuery({
    queryKey: ['reports-page', params],
    queryFn: async (): Promise<PaginatedReports> => {
      const response = await aiReportsService.getReports();
      if (!response.success) throw new Error(response.error || 'Failed to fetch reports');
      const all = response.data ?? [];
      const { status } = apiParamsFor(params.status);
      const filtered = status ? all.filter((report) => reportStatus(report) === status) : all;
      const start = (params.page - 1) * REPORTS_PAGE_SIZE;
      const results = filtered.slice(start, start + REPORTS_PAGE_SIZE);
      return {
        count: filtered.length,
        next: start + REPORTS_PAGE_SIZE < filtered.length ? String(params.page + 1) : null,
        previous: params.page > 1 ? String(params.page - 1) : null,
        results,
      };
    },
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useReportDetail(reportId: string | null) {
  return useQuery({
    queryKey: ['report-detail', reportId],
    queryFn:
      reportId == null
        ? skipToken
        : async () => {
            const response = await aiReportsService.getReport(reportId);
            if (!response.success) throw new Error(response.error || 'Failed to fetch report');
            return response.data;
          },
    staleTime: STALE_MS,
  });
}

export function useReportStatus(reportId: string | null, enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['report-status', reportId],
    queryFn: reportId == null ? skipToken : () => aiReportsService.getReportStatus(reportId),
    refetchInterval: enabled ? 4000 : false,
    enabled: reportId != null && enabled,
  });

  // The status payload carries the same is_complete/error_message pair the
  // list derives from, so the poll surfaces the same tri-state value.
  const polled = query.data?.success ? query.data.data : undefined;
  const status = polled ? reportStatus(polled) : null;

  // TanStack Query v5 has no per-query onSuccess, so the completion side
  // effect lives here: while the caller believes the report is generating
  // (enabled), any poll that shows a terminal state means the cached detail
  // and list pages are stale — invalidate both. Keying off enabled rather
  // than an observed GENERATING poll also covers generation finishing before
  // the first poll lands, where every observed status is already terminal.
  // The ref pins the invalidation to one firing per report id (re-renders and
  // repeat polls skip it) and resets when the hook is pointed at a different
  // report. A disabled hook never invalidates: opening an already-complete
  // report must not churn caches.
  const invalidated = useRef<{ id: string | null; done: boolean }>({ id: null, done: false });
  useEffect(() => {
    if (invalidated.current.id !== reportId) {
      invalidated.current = { id: reportId, done: false };
    }
    if (!enabled || reportId == null || status == null) return;
    if (invalidated.current.done) return;
    if (status === 'COMPLETED' || status === 'FAILED') {
      invalidated.current.done = true;
      queryClient.invalidateQueries({ queryKey: ['report-detail', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reports-page'] });
    }
  }, [status, enabled, reportId, queryClient]);

  return query;
}

export interface GenerateReportInput {
  request: GenerateReportRequest;
  attachments: File[];
}

export function useGenerateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ request, attachments }: GenerateReportInput): Promise<string> => {
      const response = await aiReportsService.generateReport(request);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to generate report');
      }
      // Sequential by design — attachments belong to the report the generate
      // call just created, so each upload must reference its (now-known) id.
      for (const file of attachments) {
        await aiReportsService.uploadAttachment(response.data.id, file);
      }
      return response.data.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports-page'] });
      queryClient.invalidateQueries({ queryKey: ['report-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-status'] });
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await aiReportsService.deleteReport(id);
      if (!response.success) throw new Error(response.error || 'Failed to delete report');
    },
    onSuccess: (_result, id) => {
      // Drop the cached detail outright — browser-back to the deleted
      // report's URL must not re-render it from cache within staleTime.
      queryClient.removeQueries({ queryKey: ['report-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['reports-page'] });
    },
  });
}
